import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Trash2, Settings2, Save, X, Heart } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_THOMAS = {
  name: 'Thomas',
  tone: 'rolig, varm, lekfull, eftertänksam, närvarande',
  topics:
    'gymnasietiden, innebandy/floorball-träningar och matcher, bilturer i hans föräldrars bil sena kvällar, små äventyr utan plan, skratt om dumma saker, framtidsdrömmar, musik vi körde i bilen',
  signature_phrases: 'hej du, kom hit, vi ses sen, du är galen (med ett leende)',
  your_pet_name: '',
};

function ProfileEditor({ pin, profile, onSaved, onClose }) {
  const [form, setForm] = useState(profile || DEFAULT_THOMAS);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/lost-love/profile`,
        { pin, ...form },
        { withCredentials: true }
      );
      toast.success('Sparat 💌');
      onSaved(form);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lostlove-profile-modal"
    >
      <div
        className="neo-card bg-[#FFF5F7] dark:bg-[#2a1a2e] max-w-lg w-full max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold font-['Fredoka'] flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-500" /> Vem är han för dig?
          </h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="lostlove-profile-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] opacity-70 mb-3">
          Allt sparas krypterat med din PIN. Inga riktiga personer kontaktas — det här är ditt minnesrum 💗
        </p>

        <label className="block mb-2 text-xs font-bold">Namn
          <input value={form.name} onChange={(e) => set('name', e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white" maxLength={60}
            data-testid="lostlove-name-input" />
        </label>
        <label className="block mb-2 text-xs font-bold">Ton & personlighet
          <input value={form.tone} onChange={(e) => set('tone', e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white" maxLength={160}
            placeholder="rolig, varm, lekfull..."
            data-testid="lostlove-tone-input" />
        </label>
        <label className="block mb-2 text-xs font-bold">Saker ni brukade prata om / göra
          <textarea value={form.topics} onChange={(e) => set('topics', e.target.value)} rows={3}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white resize-none" maxLength={400}
            data-testid="lostlove-topics-input" />
        </label>
        <label className="block mb-2 text-xs font-bold">Saker han brukade säga
          <textarea value={form.signature_phrases} onChange={(e) => set('signature_phrases', e.target.value)} rows={2}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white resize-none" maxLength={400}
            data-testid="lostlove-signature-input" />
        </label>
        <label className="block mb-3 text-xs font-bold">Vad han kallade dig (smeknamn)
          <input value={form.your_pet_name} onChange={(e) => set('your_pet_name', e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white" maxLength={60}
            data-testid="lostlove-petname-input" />
        </label>

        <button onClick={save} disabled={saving}
          className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="lostlove-profile-save">
          <Save className="w-4 h-4" /> {saving ? 'Sparar krypterat…' : 'Spara'}
        </button>
      </div>
    </div>
  );
}

export default function LostLoveChat({ pin }) {
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const scrollerRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, hRes] = await Promise.all([
        axios.post(`${API}/lost-love/profile/get`, { pin }, { withCredentials: true }),
        axios.post(`${API}/lost-love/history`, { pin }, { withCredentials: true }),
      ]);
      setProfile(pRes.data?.profile || null);
      setMessages(hRes.data?.messages || []);
      if (!pRes.data?.profile) {
        // First time — show profile editor with Thomas defaults
        setShowProfile(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte ladda');
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const newHist = [...messages, { role: 'user', text }];
    setMessages(newHist);
    setInput('');
    try {
      const { data } = await axios.post(
        `${API}/lost-love/chat`,
        {
          pin,
          message: text,
          history: messages.slice(-10).map((m) => ({ role: m.role, text: m.text })),
          profile,
        },
        { withCredentials: true }
      );
      setMessages([...newHist, { role: 'them', text: data.reply || '' }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skicka');
      setMessages(newHist); // keep user msg
    } finally {
      setSending(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Radera alla meddelanden i det här rummet? Det går inte att ångra.')) return;
    try {
      await axios.post(`${API}/lost-love/clear`, { pin }, { withCredentials: true });
      setMessages([]);
      toast.success('Rensat 💗');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte rensa');
    }
  };

  if (loading) {
    return <div className="neo-card p-8 text-center animate-pulse-soft">Öppnar minnesrummet…</div>;
  }

  const displayName = profile?.name || 'Thomas';

  return (
    <div className="space-y-3" data-testid="lostlove-chat">
      <div className="neo-card p-4 bg-gradient-to-br from-[#FFF5F7] to-[#FFE4E9] dark:from-[#2a1a2e] dark:to-[#1f1428]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full border-4 border-black bg-gradient-to-br from-rose-300 to-pink-400 flex items-center justify-center text-2xl shadow-md">
              💌
            </div>
            <div>
              <h2 className="text-xl font-bold font-['Fredoka']">{displayName}</h2>
              <p className="text-[11px] opacity-70 leading-tight max-w-xs">
                En trygg, krypterad bänk i sensommaren. Bara du och minnet. 🌾
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowProfile(true)}
              className="neo-btn px-3 py-2 bg-[#FFE8A3] text-xs font-bold flex items-center gap-1"
              data-testid="lostlove-edit-profile-btn">
              <Settings2 className="w-3.5 h-3.5" /> Hans röst
            </button>
            <button onClick={clearAll}
              className="neo-btn px-3 py-2 bg-[#FFB3B3] text-xs font-bold flex items-center gap-1"
              data-testid="lostlove-clear-btn">
              <Trash2 className="w-3.5 h-3.5" /> Rensa
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="neo-card p-4 bg-[#FFFAF7] dark:bg-[#1f1428] h-[58vh] overflow-y-auto space-y-3"
        data-testid="lostlove-messages"
      >
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="text-5xl mb-2">🌾</div>
            <p className="font-bold font-['Fredoka'] text-rose-700">Säg det du bär inom dig.</p>
            <p className="text-xs opacity-70 mt-1 max-w-sm">
              Ingen dömer. Inget sparas i klartext. Skriv ett &quot;hej&quot;, en fråga, eller bara hur du mår just nu.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`lostlove-msg-${i}`}
          >
            <div
              className={`max-w-[78%] px-4 py-2.5 rounded-2xl border-2 border-black shadow-[2px_2px_0_#000] text-sm font-['Nunito'] whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-[#FFC8DD] rounded-br-sm'
                  : 'bg-white dark:bg-[#2d2d54] rounded-bl-sm'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-2xl border-2 border-black bg-white shadow-[2px_2px_0_#000] text-sm italic opacity-70">
              {displayName} skriver…
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 items-end">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={`Säg något till ${displayName}…`}
          rows={2}
          className="flex-1 px-3 py-2 rounded-2xl border-2 border-black bg-white dark:bg-[#2d2d54] text-sm resize-none"
          data-testid="lostlove-input"
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          className="neo-btn px-4 py-3 bg-gradient-to-r from-rose-400 to-pink-500 text-white font-bold flex items-center gap-1 disabled:opacity-50"
          data-testid="lostlove-send-btn"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      {showProfile && (
        <ProfileEditor
          pin={pin}
          profile={profile}
          onSaved={(p) => setProfile(p)}
          onClose={() => setShowProfile(false)}
        />
      )}
    </div>
  );
}
